import type { ReactNode } from "react";
import { Config } from "@/app/config";
import type {
	AbstractLeadFormProps,
	AbstractLeadFormState,
} from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadServiceResponse, ProductType } from "@/component/assets/AbstractLeadForm/types";
import type { CalculatorError } from "@/component/assets/CalculatorError";
import { DEFAULT_PARAMS } from "@/component/Mortgage/Calculator/data";
import { calculate, responseToResult, searchParamsToRequest } from "@/component/Mortgage/Calculator/domain";
import type { MortgageRequest, MortgageResponseItem } from "@/component/Mortgage/Calculator/types";
import { GoogleAnalytics4 } from "@/shared/lib/google-analytics-4";
import type { EventComponentType } from "@/shared/lib/google-analytics-4.type";
import { JsonDataBuilder } from "@/shared/lib/JsonDataBuilder";
import { logError } from "@/shared/lib/sentry-error";
import { type CommonOptions, getUuid, http } from "@/shared/ui";
import { formatText, formatTextWhenNotZero } from "@/shared/ui/format";
import { calculateCsok, requestRealEstateInfos } from "./domain";
import * as Step from "./Steps";
import { LOAN_GOALS, SOCIAL_INSURANCE_SINCE } from "./Steps/Step1.data";
import type { CsokCalculatorRequest, CsokCalculatorResponse, CsokFormFields, CsokResponse } from "./types";

type CsokProps<
	LeadCalculatorRequest extends CsokCalculatorRequest = CsokCalculatorRequest,
	LeadCalculatorResponse extends CsokCalculatorResponse = CsokCalculatorResponse,
> = AbstractLeadFormProps<LeadCalculatorRequest, LeadCalculatorResponse>;

interface CsokState<FormFields extends CsokFormFields = CsokFormFields> extends AbstractLeadFormState<FormFields> {
	realEstateSizes: CommonOptions<number>;
	realEstateBuyings: CommonOptions<number>;
	csokResponse: CsokResponse;
	errorMessages: string[];
	mortgageRequest: MortgageRequest;
	mortgageResponse: MortgageResponseItem;
}

/**
 * @Shortcode
 *
 * @title "CSOK kalkulátor"
 * @shortcode "csok_small"
 * @slug "csok"
 * @type "calculator"
 */
export class CsokSmallCalculator extends AbstractLeadForm<
	CsokCalculatorRequest,
	CsokCalculatorResponse,
	CsokFormFields,
	CsokProps,
	CsokState
> {
	// EventParams
	protected readonly productTypes: string = "CSOK-CSOK,MORTGAGE-CSOK,CSOK";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		outsideTitle: "Mennyi CSOK-ot kaphatsz?",
		outsideTitleLink: "/csok-feltetelei/",
		bottomLogo: true,
	};

	protected getInitialState(): CsokState {
		return {
			...super.getInitialState(),
			activeStep: Config.getLeadSource() === "PENZCENTRUM" ? "custom-start-screen" : "step-1",
			realEstateSizes: [],
			realEstateBuyings: [],

			csokResponse: {
				apr: undefined,
				canGetCsok: undefined,
				canGetLoan: undefined,
				csokAmount: undefined,
				errorMessages: [],
				installmentStart: undefined,
				loanErrorMessages: [],
				maturity: undefined,
			},

			mortgageResponse: undefined,
			errorMessages: [],
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values,
			submitState,
			realEstateSizes,
			realEstateBuyings,
			csokResponse,
			errorMessages,
			mortgageRequest,
			mortgageResponse,
			leadId,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" submitState={submitState} bottomLogo />
				<Step.Step2
					stepName="step-2"
					defaultTarget="calculate-csok"
					realEstateSizes={realEstateSizes}
					realEstateBuyings={realEstateBuyings}
					loanGoal={values.loanGoal}
					submitState={submitState}
					canStepBack
				/>
				<Step.ResultUnsuitable
					stepName="result-unsuitable"
					defaultTarget="success-subscription"
					loanGoal={values.loanGoal}
					realEstateValue={values.realEstateValue}
					numberOfChildren={values.numberOfChildren}
					errorMessages={csokResponse?.errorMessages}
					submitState={submitState}
					leadId={leadId}
					canStepBack
				/>
				<Step.SuccessSubscription stepName="success-subscription" canStepBack />
				<Step.ResultSuitable
					stepName="result-suitable"
					defaultTarget="step-4"
					csokAmount={csokResponse?.csokAmount}
					numberOfChildren={this.getTotalNumberOfChildren()}
					canStepBack
				/>

				<Step.Step4
					stepName="step-4"
					defaultTarget="step-5"
					realEstateValue={values.realEstateValue}
					canStepBack
				/>
				<Step.Step5
					stepName="step-5"
					defaultTarget="calculate-mortgage"
					submitState={submitState}
					canStepBack
				/>
				<Step.LeadFormCsok
					stepName="lead-form-csok"
					defaultTarget="success-lead"
					submitState={submitState}
					canStepBack
				/>
				<Step.SuccessPage
					stepName="success-lead"
					subscriptionTypes={["RETAIL_NEWSLETTER"]}
					userEmail={values.email}
					canStepBack
				/>
				<Step.ResultCsokOnly
					stepName="result-csok-only"
					defaultTarget="lead-form"
					errorMessages={errorMessages}
					csokAmount={csokResponse?.csokAmount}
					canStepBack
				/>
				<Step.Result
					stepName="result-csok-mortgage"
					defaultTarget="lead-form"
					loanAmount={values.loanAmount}
					maturity={mortgageRequest?.maturity}
					result={mortgageResponse}
					canStepBack
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-lead"
					submitState={submitState}
					variant={undefined}
					canStepBack
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "step-2":
				this.onStep2(target);
				break;
			case "calculate-csok":
				this.onCalculateCsok();
				break;
			case "calculate-mortgage":
				this.onCalculateMortgage();
				break;
			case "success-subscription":
				this.onSuccessSubscription();
				break;
			case "success-lead":
				this.sendLead()
					.then(() => {
						super.onStep(target, false);
						this.sendViewItemListEvent(target);
					})
					.catch((error: Error) => {
						// TODO: Hibakezelés
						logError(error);
					});
				break;
			default:
				super.onStep(target, withEvent);
				this.sendViewItemListEvent(target);
		}
	}

	protected sendLead(): Promise<LeadServiceResponse> {
		const { ga4Event, response: responseProps } = this.props;
		const { mortgageResponse, values } = this.state;
		this.setState({
			submitState: "loading",
		});

		this.bmLeadGa4EventUuid = getUuid();
		const leadData = this.getLeadData();
		const templateName = this.getAutoresponderName();

		if (templateName) {
			leadData.autoresponder = {
				templateName,
				fields: this.getAutoresponderFields(),
			};
		}

		if (!leadData.request?.productRequisitionTime?.length) {
			leadData.request.productRequisitionTime = undefined;
		}

		if (mortgageResponse) {
			leadData.calculatorType = "CSOK";
			leadData.productSubType = "CSOK";
			leadData.administrationLocation = leadData.cityId;
		}

		this.sendEvent("BM_lead", {
			buttonId: ga4Event?.buttonId || GoogleAnalytics4.generateId("bi"),
			leadUuid: this.bmLeadGa4EventUuid,
			position: ga4Event?.position || 1,
			leadType: leadData.productType,
			...values,
			user_data: {
				email_address: values.email,
				phone_number: values.phone,
				address: {
					last_name: values.lastName,
					first_name: values.firstName,
					city: values.cityName,
				},
			},
			productName: responseProps?.productName,
		});

		return this.promisePool
			.add(http.post(Config.getWpRestApiUrl("/lead"), leadData))
			.then((response: LeadServiceResponse) => {
				if (!response?.leadId) {
					throw new Error("Lead ID is missing");
				}

				if (!mortgageResponse) {
					return response;
				}

				leadData.leadUuid = getUuid();
				leadData.calculatorType = "MORTGAGE";
				leadData.productType = "MORTGAGE";
				leadData.productSubType = "CSOK";

				this.sendEvent("BM_lead", {
					buttonId: ga4Event?.buttonId || GoogleAnalytics4.generateId("bi"),
					leadUuid: leadData.leadUuid,
					position: ga4Event?.position || 1,
					leadType: leadData.productType,
					...values,
					user_data: {
						email_address: values.email,
						phone_number: values.phone,
						address: {
							last_name: values.lastName,
							first_name: values.firstName,
							city: values.cityName,
						},
					},
					productName: responseProps?.productName,
				});

				return this.promisePool.add(http.post(Config.getWpRestApiUrl("/lead"), leadData));
			})
			.then((response: LeadServiceResponse) => {
				if (!response?.leadId) {
					throw new Error("Lead ID is missing");
				}

				this.onSent(response);

				this.setState({
					submitState: "done",
				});

				return response;
			})
			.catch((reason) => {
				this.setState({
					submitState: "error",
				});

				throw reason;
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});
			});
	}

	private onStep2(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(requestRealEstateInfos(values))
			.then(([responseSizes, responseBuyings]) => {
				this.setState(
					{
						submitState: undefined,
						realEstateSizes: responseSizes.map((item) => ({
							value: item.id,
							label: item.name,
						})),
						realEstateBuyings: responseBuyings.map((item) => ({
							value: item.id,
							label: item.name,
						})),
					},
					() => {
						super.onStep(target, true);
						this.sendViewItemListEvent(target);
					},
				);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onCalculateCsok(): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(calculateCsok(values))
			.then((response: CsokResponse) => {
				this.setState(
					{
						submitState: undefined,
						csokResponse: response,
					},
					() => {
						const target = response.canGetCsok ? "result-suitable" : "result-unsuitable";
						super.onStep(target, true);
						this.sendViewItemListEvent(target);
					},
				);

				this.sendCalculationEvent(response.errorMessages);
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onCalculateMortgage(): void {
		const {
			values: {
				loanGoal,
				loanAmount,
				incomeList,
				realEstateValue,
				numberOfChildren,
				minEmploymentRelationship,
				inKHR,
			},
		} = this.state;
		this.setState({ submitState: "loading" });

		const searchParams = {
			...DEFAULT_PARAMS,
			loanGoal,
			loanAmount,
			// Maturity: csokResponse.maturity,
			incomeList,
			onePerBank: false,
			realEstateValue,
			numberOfChildren,
		};

		const errors = [];

		if (!minEmploymentRelationship) {
			errors.push("Lakáshitelhez legalább 3 hónap munkaviszonyt kell igazolnod a jelenlegi munkahelyedről.");
		}

		if (inKHR) {
			errors.push("Nem kaphatsz lakáshitelt, amíg a KHR (BAR) listában szerepelsz.");
		}

		const request = searchParamsToRequest(searchParams, "THM");

		this.promisePool
			.add(calculate(request))
			.then((response) => {
				const results = responseToResult(request, response, "without-ct");

				if (results?.normalList?.length) {
					this.setState({
						mortgageResponse: results.normalList[0],
						mortgageRequest: request,
					});
				} else if (!errors.length) {
					errors.push("Nincs számodra megfelelő a bankok kínálatai között.");
				}
			})
			.catch((error: CalculatorError) => {
				if (error?.fieldErrors) {
					Object.values(error.fieldErrors).forEach((errorMsg: string) => {
						errors.push(errorMsg);
					});
				}

				if (error?.message) {
					errors.push(error.message);
				} else {
					errors.push("Ismeretlen hiba történt a kalkuláció közben!");
					logError(error);
				}
			})
			.finally(() => {
				this.setState(
					{
						submitState: undefined,
						errorMessages: errors,
					},
					() => {
						const target = errors.length ? "result-csok-only" : "result-csok-mortgage";
						super.onStep(target, true);
						this.sendViewItemListEvent(target);
					},
				);

				this.sendCalculationEvent(errors);
			});
	}

	private onSuccessSubscription(): void {
		// TODO: Hamarosan, de csak ha partnermódban van
	}

	private getTotalNumberOfChildren(): number {
		const {
			values: { loanGoal, numberOfChildren, numberOfChildrenCommitment, married, socialInsuranceSinceId },
		} = this.state;

		if (loanGoal === "BOVITES") {
			return 1;
		}

		// Ez nem tényleges gyerekek száma, csak hogy hány gyerekes csokra jogosult a felhasználó
		let totalNumberOfChildren = numberOfChildren;

		if (married) {
			totalNumberOfChildren += numberOfChildrenCommitment;
		}

		if (
			socialInsuranceSinceId === 2 &&
			(loanGoal === "UJ_LAKAS" || loanGoal === "EPITES") &&
			totalNumberOfChildren > 2
		) {
			totalNumberOfChildren = 2;
		}

		return totalNumberOfChildren;
	}

	protected getCalculatorRequest(): CsokCalculatorRequest {
		const {
			realEstateSizes,
			realEstateBuyings,
			values: {
				loanGoal,
				married,
				numberOfChildren,
				numberOfChildrenCommitment,
				taxDept,
				socialInsuranceSinceId,
				realEstateValue,
				realEstateSizeId,
				ownRealEstateEnlargement,
				realEstateBuying,
				buyFromCloseRelative,
				loanAmount,
				minEmploymentRelationship,
				inKHR,
				incomeList,
				earlierBoughtRealEstate,
				productRequisitionTime,
				question,
			},
		} = this.state;

		const request: CsokCalculatorRequest = {
			...super.getCalculatorRequest(),
			numberOfChildrenCommitment,
			realEstateSize: realEstateSizeId,
			realEstateSizeName: realEstateSizes.find((item) => item.value === realEstateSizeId).label,
			taxDept,
			socialInsuranceSince: socialInsuranceSinceId,
			socialInsuranceSinceName: SOCIAL_INSURANCE_SINCE.find((item) => item.value === socialInsuranceSinceId)
				.label,
			buyFromCloseRelative,
			ownRealEstateEnlargement,
			earlierBoughtRealEstate,
			needMortgageLoan: loanAmount > 0,
			productRequisitionTime,
			loanGoal,
			loanGoalName: LOAN_GOALS.find((item) => item.value === loanGoal)?.label,
			married,
			numberOfChildren,
			realEstateValue,
			inKHR,
			loanAmount,
			minEmploymentRelationship,
			incomeList,
			question,
		};

		const realEstateName = realEstateBuyings.find((item) => item.value === realEstateBuying).label;
		switch (loanGoal) {
			case "EPITES":
				request.realEstateBuilding = realEstateBuying;
				request.realEstateBuildingName = realEstateName;
				break;
			case "BOVITES":
				request.realEstateEnlargement = realEstateBuying;
				request.realEstateEnlargementName = realEstateName;
				break;
			default:
				request.realEstateBuying = realEstateBuying;
				request.realEstateBuyingName = realEstateName;
		}

		return request;
	}

	protected getLeadDataRequest(): CsokCalculatorRequest {
		const {
			values: { married, taxDept },
		} = this.state;
		const request = this.getCalculatorRequest();

		const otherData = new JsonDataBuilder(request.otherDataJson);
		otherData.addItem("Házasként igényled a támogatást", "married", married, "bool");
		otherData.addItem("Van adótartozásod", "taxDept", taxDept, "bool");

		request.otherDataJson = otherData.getJson();

		return request;
	}

	protected getCalculatorResponse(): CsokCalculatorResponse {
		const {
			csokResponse,
			csokResponse: { csokAmount, maturity, apr, installmentStart },
		} = this.state;

		const response = { ...super.getCalculatorResponse() };

		if (csokResponse) {
			return {
				...response,
				canGetCsok: csokAmount > 0,
				csokAmount,
				maturity,
				apr,
				installmentStart,
			};
		}

		return response;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-csok";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			mortgageResponse,
			values: { lastName, firstName, loanAmount },
			csokResponse: { csokAmount },
		} = this.state;
		const expenses =
			mortgageResponse?.expenseGroups !== undefined
				? mortgageResponse.expenseGroups?.MODIFICATION_FEES?.expenses
				: undefined;
		let interestRate: number;
		let interestPeriodName = "";

		if (mortgageResponse?.loanType === "marketLoan") {
			interestPeriodName = mortgageResponse?.interestPeriodNotAided.name;
			interestRate = mortgageResponse?.interestNotAided;
		} else {
			interestPeriodName = `Támogatás alatt:<br />${
				mortgageResponse?.interestPeriodAided?.name || " - "
			}<br />Támogatás után:<br />${mortgageResponse?.interestPeriodNotAided?.name || " - "}`;
			interestRate = mortgageResponse?.interestAided;
		}

		return {
			lastName,
			firstName,
			needMortgageLoan: loanAmount > 0,
			csokAmount: formatText(csokAmount, "ft"),
			loanAmount: formatText(loanAmount, "ft"),
			installmentStart: formatText(mortgageResponse?.installmentStart, "ft"),
			interestRate: formatText(interestRate, "%"),
			interestPeriodName,
			allRecovered: formatText(mortgageResponse?.allRecovered, "ft"),
			ETKALTexpenseString: expenses?.ETKALT?.expenseString,
			apr: formatText(mortgageResponse?.apr, "%"),
			ETKONexpenseString: expenses?.ETKON?.expenseString,
			ETKMHexpenseString: expenses?.ETKMH?.expenseString,
			VTKALTexpenseString: expenses?.VTKALT?.expenseString,
			VTKONexpenseString: expenses?.VTKON?.expenseString,
			VTKMHexpenseString: expenses?.VTKMH?.expenseString,
			startUpCost: formatText(mortgageResponse?.startUpCost, "ft"),
			startUpCostRefund: formatTextWhenNotZero(mortgageResponse?.startUpCostRefund, "ft"),
			interestDiscount: formatTextWhenNotZero(mortgageResponse?.bmDiscounts?.interestDiscount, "ft"),
			earlyRepaymentDiscountInGivenMonth: formatTextWhenNotZero(
				mortgageResponse?.bmDiscounts?.earlyRepaymentDiscountInGivenMonth,
				"ft",
			),
			totalSavings: formatTextWhenNotZero(mortgageResponse?.bmDiscounts?.totalSavings, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "CSOK";
	}

	protected getClassName(): string {
		return `${super.getClassName()} csok-leadform`;
	}

	private sendViewItemListEvent(target: string): void {
		if (!["result-suitable", "result-csok-only", "result-csok-mortgage"].includes(target)) {
			return;
		}

		const { csokResponse, mortgageResponse } = this.state;

		this.sendEvent("view_item_list", {
			item_list_id: this.calculationId,
			item_list_name: "normal",
			items: [
				{
					item_id: "",
					item_brand: "",
					item_name: "",
					affiliation: "product",
					index: 1,
					coupon: "none",
					currency: "HUF",
					discount: mortgageResponse?.bmDiscounts?.totalSavings || 0,
					item_category: csokResponse?.errorMessages?.join("|"),
					item_category2: "",
					item_category3: "",
					item_category4: "",
					item_category5: "",
					item_variant: "",
					price: csokResponse?.csokAmount,
					quantity: undefined,
				},
			],
		});
	}

	protected getComponentName(): string {
		return "CsokSmallCalculator";
	}
}

export default CsokSmallCalculator;
